/**
 * PaymentTypeSelector Component
 *
 * Radio button selector for choosing between monthly (one-time) and annual (recurring) payment types.
 * Displays pricing information for each option.
 *
 * @module components/subscription/PaymentTypeSelector
 */

/**
 * Payment type options
 */
export type PaymentType = 'monthly' | 'annual';

/**
 * Props for the PaymentTypeSelector component
 */
export interface PaymentTypeSelectorProps {
  /** Currently selected payment type */
  selectedType: PaymentType;
  /** Callback when payment type changes */
  onChange: (type: PaymentType) => void;
  /** Monthly price in EUR */
  monthlyPrice: number;
  /** Annual price per month in EUR */
  annualPricePerMonth: number;
  /** Annual total price in EUR */
  annualTotalPrice: number;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * Format price for display in EUR
 */
function formatPrice(price: number): string {
  return price.toFixed(2).replace('.', ',') + ' €';
}

/**
 * Calculate savings percentage for annual plan
 */
function calculateSavings(monthlyPrice: number, annualPricePerMonth: number): number {
  if (monthlyPrice <= 0) return 0;
  return Math.round(((monthlyPrice - annualPricePerMonth) / monthlyPrice) * 100);
}

/**
 * PaymentTypeSelector Component
 *
 * Allows user to choose between monthly and annual payment options.
 * Highlights savings for annual subscription.
 */
export function PaymentTypeSelector({
  selectedType,
  onChange,
  monthlyPrice,
  annualPricePerMonth,
  annualTotalPrice,
  disabled = false,
}: PaymentTypeSelectorProps) {
  const savings = calculateSavings(monthlyPrice, annualPricePerMonth);

  return (
    <div className="space-y-3">
      {/* Annual Option - First (recommended) */}
      <label
        className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all relative ${
          selectedType === 'annual'
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {/* Savings Badge */}
        {savings > 0 && (
          <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            -{savings}%
          </div>
        )}

        <div className="flex items-center gap-3">
          <input
            type="radio"
            name="payment-type"
            value="annual"
            checked={selectedType === 'annual'}
            onChange={() => onChange('annual')}
            disabled={disabled}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
          />
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              Annuel
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Abonnement récurrent, annulable à tout moment
            </div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-bold text-lg text-gray-900 dark:text-white whitespace-nowrap">
            {formatPrice(annualTotalPrice)}
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/an</span>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            soit {formatPrice(annualPricePerMonth)}/mois
          </div>
        </div>
      </label>

      {/* Monthly Option */}
      <label
        className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
          selectedType === 'monthly'
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center gap-3">
          <input
            type="radio"
            name="payment-type"
            value="monthly"
            checked={selectedType === 'monthly'}
            onChange={() => onChange('monthly')}
            disabled={disabled}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
          />
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              Mensuel
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Paiement unique, renouvelable manuellement
            </div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-bold text-lg text-gray-900 dark:text-white whitespace-nowrap">
            {formatPrice(monthlyPrice)}
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/mois</span>
          </div>
        </div>
      </label>
    </div>
  );
}

export default PaymentTypeSelector;
